import type { HomeyAPI, Experiment } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors';
import { useAttach } from '../../../store/useAttach';

export interface State {
  experimentsData: ExperimentsData | null;
  isLoading: boolean;
  error: unknown;
}

type ExperimentsData = { [key: string]: Experiment };

export type ExperimentsSettingsStoreState = State;

class ExperimentsSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('experiments-settings');
  }

  override createInitialState() {
    return {
      experimentsData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const experimentsData = await api.experiments.getExperiments();

      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        experimentsData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  onAttached({ api }: { api: HomeyAPI }): void {}

  onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'experiments-settings');

    return { experimentsSettings: this.store(selectAll, shallow) };
  }

  async enableExperiment({ id }: { id: string }) {
    const api = this.getApi();
    const result = await api.experiments.enableExperiment({ id });

    this.set((state) => {
      const currentData = state.experimentsData?.[id];
      // Somehow we have no data for this experiment.
      if (currentData == null) return {};

      return {
        experimentsData: {
          ...state.experimentsData,
          [id]: {
            ...currentData,
            enabled: true,
          },
        },
      };
    });

    return result;
  }

  async disableExperiment({ id }: { id: string }) {
    const api = this.getApi();
    const result = await api.experiments.disableExperiment({ id });

    this.set((state) => {
      const currentData = state.experimentsData?.[id];
      // Somehow we have no data for this experiment.
      if (currentData == null) return {};

      return {
        experimentsData: {
          ...state.experimentsData,
          [id]: {
            ...currentData,
            enabled: false,
          },
        },
      };
    });

    return result;
  }
}

function selectAll(state: ExperimentsSettingsStoreState) {
  return {
    ...selectBase(state),
    experimentsData: state.experimentsData,
  };
}

export const experimentsSettingsStore = new ExperimentsSettingsStore();
